import { Skeleton } from '@heidi/design-system/skeleton';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { useIntl, type IntlShape, type MessageDescriptor } from 'react-intl';

import { objectWorkspaceMessages as messages } from './ObjectWorkspace.messages';
import type {
  ObjectWorkspacePatientDetails,
  ObjectWorkspacePatientDetailsState,
} from './objectWorkspace.data';

interface PatientDetailFieldProps {
  label: MessageDescriptor;
  value?: string | null;
}

function PatientDetailField({ label, value }: PatientDetailFieldProps): JSX.Element {
  const intl = useIntl();
  const visibleValue = value?.trim();

  return (
    <div className="min-w-0">
      <TypographyCaption className="text-text-tertiary">
        {intl.formatMessage(label)}
      </TypographyCaption>
      <TypographyP2
        className={cn(
          'mt-1 min-w-0 whitespace-pre-wrap break-words text-text-primary',
          !visibleValue && 'text-text-tertiary'
        )}
      >
        {visibleValue || intl.formatMessage(messages.emptyPatientDetail)}
      </TypographyP2>
    </div>
  );
}

function PatientDetailSkeleton({ multiline = false }: { multiline?: boolean }) {
  return (
    <div className="min-w-0">
      <Skeleton className="h-3 w-20" />
      <Skeleton
        className={cn('mt-2 w-full', multiline ? 'h-14' : 'h-5')}
        data-testid="patient-detail-skeleton"
      />
    </div>
  );
}

function PatientDetailsLoading(): JSX.Element {
  const intl = useIntl();

  return (
    <section
      aria-busy="true"
      aria-label={intl.formatMessage(messages.loadingPatientDetails)}
      className="flex flex-col gap-5"
    >
      <div className="grid min-w-0 grid-cols-2 gap-4">
        <PatientDetailSkeleton />
        <PatientDetailSkeleton />
      </div>
      <div className="grid min-w-0 grid-cols-2 gap-4">
        <PatientDetailSkeleton />
        <PatientDetailSkeleton />
      </div>
      <PatientDetailSkeleton multiline />
      <PatientDetailSkeleton multiline />
      <PatientDetailSkeleton multiline />
      <PatientDetailSkeleton multiline />
    </section>
  );
}

function formatBirthDate(intl: IntlShape, value?: string | null): string | null {
  const isoDate = value?.trim();
  if (!isoDate) return null;

  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) return isoDate;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  // JS Date overflows invalid calendars (e.g. 1984-13-45); require a round-trip.
  if (
    Number.isNaN(date.getTime()) ||
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return isoDate;
  }

  return intl.formatDate(date, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatGender(intl: IntlShape, value?: string | null): string | null {
  switch (value?.trim().toLowerCase()) {
    case 'female':
      return intl.formatMessage(messages.genderFemale);
    case 'male':
      return intl.formatMessage(messages.genderMale);
    case 'other':
      return intl.formatMessage(messages.genderOther);
    case 'unknown':
      return intl.formatMessage(messages.genderUnknown);
    default:
      return value?.trim() || null;
  }
}

function PatientDetails({ details }: { details: ObjectWorkspacePatientDetails }) {
  const intl = useIntl();

  return (
    <section className="flex min-w-0 flex-col gap-5">
      <div className="grid min-w-0 grid-cols-2 gap-4">
        <PatientDetailField
          label={messages.dateOfBirth}
          value={formatBirthDate(intl, details.birthDate)}
        />
        <PatientDetailField
          label={messages.gender}
          value={formatGender(intl, details.gender)}
        />
      </div>
      <div className="grid min-w-0 grid-cols-2 gap-4">
        <PatientDetailField label={messages.email} value={details.email} />
        <PatientDetailField label={messages.phoneNumber} value={details.phone} />
      </div>
      <PatientDetailField
        label={messages.pastMedicalHistory}
        value={details.pastMedicalHistory}
      />
      <PatientDetailField
        label={messages.currentMedications}
        value={details.currentMedications}
      />
      <PatientDetailField label={messages.allergies} value={details.allergies} />
      <PatientDetailField
        label={messages.additionalNotes}
        value={details.additionalNotes}
      />
    </section>
  );
}

export function ObjectWorkspacePatientDetails({
  state,
}: {
  state: ObjectWorkspacePatientDetailsState;
}): JSX.Element {
  const intl = useIntl();

  if (state.status === 'loading') return <PatientDetailsLoading />;
  if (state.status === 'error') {
    return (
      <TypographyP2 className="text-text-secondary">
        {intl.formatMessage(messages.patientDetailsUnavailable)}
      </TypographyP2>
    );
  }
  return <PatientDetails details={state.details} />;
}
